import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { Observable } from 'rxjs/internal/Observable';
import { guid } from 'src/app/_helpers/utility';
import { MitFieldInfo } from 'src/app/_models/body';
import { Global } from 'src/app/_services/global.service';
import { InputFieldType } from 'src/app/common/constants';
import { SubTabTableComponent } from '../sub-tab-table/sub-tab-table.component';
import { environment } from 'src/environments/environment';
import { MmitaAssetService } from 'src/app/_services/mmitaAsset.service';

@Component({
  selector: 'app-library-item',
  templateUrl: './library-item.component.html',
  styleUrls: ['./library-item.component.css']
})
export class LibraryItemComponent implements OnInit {
  data: any;
  formGroup: FormGroup;
  dialogFields: Array<MitFieldInfo>
  callback
  readonly createModeGuid = guid()
  constructor(
    private global: Global,
    private nzModalRef: NzModalRef,
    private mmitaAssetService: MmitaAssetService
  ) { }

  get subTabs(): FormArray {
    return this.formGroup.get('subTabs') as FormArray;
  }

  // 自定义验证器函数
  urlEventKeyValidator(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // 如果url为空，直接通过验证
      }

    const eventKeyPattern = /&EventKey=[^&]+/;
      const isValid = eventKeyPattern.test(control.value);

      return isValid ? null : {
        eventKeyRequired: {
          message: 'When the Tab type is Event, the URL must contain the &EventKey=parameter',
        }
      };
    };
  }

  ngOnInit(): void {
    this.formGroup = new FormGroup({
      tabType: new FormControl(this.data?.tabType, Validators.required),
      categoryId: new FormControl(this.data?.categoryId || this.createModeGuid, Validators.required),
      categoryName: new FormControl(this.data?.categoryName, Validators.required),
      localizationKey: new FormControl(this.data?.localizationKey, Validators.required),
      order: new FormControl(this.data?.order, Validators.required),
      maxSize: new FormControl(this.data?.maxSize, Validators.required),
      url: new FormControl(this.data ? this.data?.url : `${environment.MAIN_API_ENDPOINT}api/Asset/Query?CategoryId=${this.createModeGuid}&PageSize=20`),
      subTabs: new FormArray(
        (this.data?.subTabs || []).map((subTab: any) =>
          new FormGroup({
            name: new FormControl(subTab?.name),
            localizationKey: new FormControl(subTab?.localizationKey),
            order: new FormControl(subTab?.order),
            url: new FormControl(subTab?.url),
            queryParams: new FormControl(subTab?.queryParams)
          })
        )
      )
    });
    // 监听tabType的变化，动态添加/移除验证器
    const urlControl = this.formGroup.get('url');
    const tabTypeControl = this.formGroup.get('tabType');

    if (urlControl && tabTypeControl) {
      tabTypeControl.valueChanges.subscribe((tabType: string) => {
        if (tabType === 'Event') {
          // 当tabType为Event时，添加验证器
          urlControl.setValidators(this.urlEventKeyValidator());
        } else {
          // 当tabType不是Event时，移除所有验证器
          urlControl.setValidators(null);
        }
        // 更新验证状态
        urlControl.updateValueAndValidity();
      });

      // 初始化时根据当前值设置验证器
      const initialTabType = tabTypeControl.value;
      if (initialTabType === 'Event') {
        urlControl.setValidators(this.urlEventKeyValidator());
      }
    }
    this.dialogFields = LibraryItem(this.global.section.AssetLibraryTabTypeSelections)
    this.callback = () => this.onSubmit.call(this)
  }

  addSubTab() {
    this.subTabs.push(
      new FormGroup({
        name: new FormControl(''),
        localizationKey: new FormControl(''),
        order: new FormControl(0),
        url: new FormControl(''),
        queryParams: new FormControl('')
      })
    );
  }

  removeSubTab(index: number) {
    this.subTabs.removeAt(index);
  }


  onSubmit() {
    return new Observable(sub => {
      const tabType = this.formGroup.get('tabType')?.value;
      const url = this.formGroup.get('url')?.value;

      // 如果是Event类型，进行服务端验证
      if (tabType === 'Event' && url) {
        this.mmitaAssetService.getEventCategories().subscribe({
          next: (categories) => {
            const eventKeys = Object.keys(categories || {});
            const eventKeyMatch = url.match(/&EventKey=([^&]*)/);

            if (!eventKeyMatch) {
              this.formGroup.get('url')?.setErrors({
                eventKeyRequired: {
                  message: 'When the Tab type is Event, the URL must contain the &EventKey=parameter'
                }
              });
              sub.error('EventKey参数缺失');
              return;
            }

            const eventKey = eventKeyMatch[1];
            if (!eventKeys.includes(eventKey)) {
              this.formGroup.get('url')?.setErrors({
                eventKeyRequired: {
                  message: `The EventKey "${eventKey}" does not exist. Please go to the Event Category to find an existing property key`
                }
              });
              sub.error(`The EventKey "${eventKey}" does not exist. Please go to the Event Category to find an existing property key`);
              return;
            }

            // 验证通过才提交
            this.nzModalRef.triggerOk();
            sub.next();
            sub.complete();
          },
          error: (error) => {
            // API调用失败时的处理
            sub.error('验证EventKey时发生错误');
          }
        });
      } else {
        // 非Event类型直接提交
        this.nzModalRef.triggerOk();
        sub.next();
        sub.complete();
      }
    });
  }
}


const LibraryItem = (AssetLibraryTabTypeSelections): Array<MitFieldInfo> => {
  return [
    {
      key: 'tabType',
      label: 'Tab Type',
      type: InputFieldType.Selector,
      options: AssetLibraryTabTypeSelections,
      size: 6,
      required: true
    },
    // {
    //   key: 'categoryId',
    //   label: 'Category ID',
    //   type: InputFieldType.Text,
    //   size: 12
    // },
    {
      key: 'categoryName',
      label: 'Category Name',
      type: InputFieldType.Text,
      size: 6,
      required: true
    },
    {
      key: 'localizationKey',
      label: 'Localization Key (needs to be manually added to the translation Excel)',
      type: InputFieldType.Text,
      size: 12,
      required: true
    },
    {
      key: 'order',
      label: 'Order',
      type: InputFieldType.Number,
      size: 6,
      required: true
    },
    {
      key: 'maxSize',
      label: 'Max Size',
      type: InputFieldType.Number,
      size: 6,
      required: true
    },
    {
      key: 'url',
      label: `URL (If TabType=Event, please include &EventKey={eventCategoryPropertyKey} in the url)`,
      type: InputFieldType.Text,
      size: 24,
      showValidation: true,
      errorKey: 'eventKeyRequired'
    },

    {
      key: 'subTabs',
      label: 'Sub Tabs',
      type: InputFieldType.CustomComponent,
      size: 24,
      component: SubTabTableComponent
    }

  ];

}